import { ExecStatus, ExecStepFileChangeType, JobSource, WebhookType } from './enums.interface';
// ExecJob lives in exec.interface.ts. This file used to declare a second copy
// that had already drifted (`arguments?: string` vs `string | null`, and so on
// for every nullable column), so which one a caller got depended on which
// module it imported from. Re-exported here to keep existing import sites
// working (EHQ-134).
import type { ExecJob, ReviewFileRef } from './exec.interface';

export { ExecStatus, ExecStepFileChangeType, JobSource, WebhookType };
export type { ExecJob, ReviewFileRef };

export enum JobListSortColumns {
  Status = 'status',
  Source = 'source',
  CreatedAt = 'createdAt',
  ScheduledAt = 'scheduledAt',
  FinishedAt = 'finishedAt'
}

/**
 * A routine for executing a workflow (RoutineDetails from server).
 * Server serializes with `rename_all = "camelCase"`.
 */
export interface ExecPlan {
  routineId: number;
  userId: number;
  projectId: number;
  workflowId?: number;
  workflowName?: string;
  runsAsId: number;
  paused: boolean;
  source: JobSource;
  webhookType?: WebhookType;
  webhookSecretId1?: number;
  webhookSecretId2?: number;
  webhookCode?: string;
  createdAt: string;
  modifiedAt: string;
  crontab?: string;
  name: string;
  description: string;
  maxRetries: number;
  consecutiveRetries: number;
  useHead: boolean;
  /** Routine-scoped job arguments as a JSON object string (default "{}"). */
  arguments?: string;
  // Fields from RoutineDetails (joined from view)
  projectName: string;
  versionId?: number;
  majorVersion?: number;
  minorVersion?: number;
  runsAsName: string;
  userName: string;
}

/**
 * Response wrapper returned by create/update routine endpoints.
 * Server sends `{ routine: RoutineDetails, job?: ExecJob }`.
 */
export interface ExecPlanAndJob {
  routine: ExecPlan;
  job?: ExecJob;
}

/**
 * Request body for creating a routine.
 * Server expects camelCase (NewRoutineParams has `rename_all = "camelCase"`).
 */
export interface CreatePlanRequest {
  name: string;
  description: string;
  /** Binds the routine to a project version (required by server validation in many environments). */
  versionId?: number;
  workflowId?: number;
  workflowName?: string;
  runsAsId?: number;
  crontab?: string;
  source?: JobSource;
  webhookType?: WebhookType;
  webhookSecretId1?: number;
  webhookSecretId2?: number;
  useHead?: boolean;
  /** Routine-scoped job arguments as a JSON object string. */
  arguments?: string;
}

/**
 * Request body for updating a routine.
 * Server expects camelCase (RoutineUpdateParams has `rename_all = "camelCase"`).
 */
export interface UpdatePlanRequest {
  name?: string;
  workflowId?: number;
  workflowName?: string;
  runsAsId?: number;
  crontab?: string;
  description?: string;
  paused?: boolean;
  maxRetries?: number;
  source?: JobSource;
  webhookType?: WebhookType;
  webhookSecretId1?: number;
  webhookSecretId2?: number;
  useHead?: boolean;
  requiresReview?: boolean;
  onApproveRoutineId?: number | null;
  onRejectRoutineId?: number | null;
  /** Routine-scoped job arguments as a JSON object string ("{}" clears stored args). */
  arguments?: string;
}

/**
 * Request body for creating a new job.
 * Server expects snake_case (NewJobParams has NO rename_all).
 */
export interface CreateJobRequest {
  runs_as_id?: number;
  arguments?: string;
  scheduled_time?: string;
  source?: JobSource;
  use_head?: boolean;
}

/**
 * Request body for triggering a routine.
 * Server expects snake_case (TriggerParams has NO rename_all).
 */
export interface TriggerRoutineRequest {
  runs_as_id?: number;
  arguments?: string;
  source?: JobSource;
  use_head?: boolean;
}

/**
 * Request body for retrying a job.
 * Server expects camelCase (JobRetryParams has `rename_all = "camelCase"`).
 */
export interface RetryJobRequest {
  scheduledTime?: string;
  runsAsId?: number;
}

export interface JobListParams {
  status?: ExecStatus | ExecStatus[];
  source?: JobSource | JobSource[];
  sort?: JobListSortColumns;
  offset?: number;
  limit?: number;
  desc?: boolean;
  runsAsId?: number;
  startedSince?: string;
}

/**
 * A step within a job's execution.
 * Server serializes with `rename_all = "camelCase"`.
 */
export interface ExecStep {
  execStepId: number;
  stepId: number;
  jobId: number;
  stepIndex: number;
  status: ExecStatus;
  startedAt?: string;
  finishedAt?: string;
  envDiff?: string;
  results?: string;
  renders?: string;
  logs?: string;
  messages?: string;
  durationMs?: number;
  errorDetails?: string;
}

/**
 * A file change produced by a step.
 * Server serializes with `rename_all = "camelCase"`.
 * Note: etag and s3path are skip_serializing on server, so never sent.
 */
export interface ExecStepFile {
  execStepId: number;
  jobId: number;
  fileId: number;
  changeType: ExecStepFileChangeType;
  name: string;
  filesize: number;
  modifiedAt: string;
  stepIndex: number;
}

export interface RoutineValidationResponse {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface RoutineStats {
  routineId: number;
  totalJobs: number;
  pendingJobs: number;
  scheduledJobs: number;
  executingJobs: number;
  completedJobs: number;
  failedJobs: number;
  canceledJobs: number;
  retryJobs: number;
  abortedJobs: number;
  avgDurationMs: number | null;
  lastSuccessfulRun: string | null;
  lastFailedRun: string | null;
  nextScheduledRun: string | null;
}

export interface RoutineHistoryItem {
  jobId: number;
  status: ExecStatus;
  source: JobSource;
  scheduledTime: string;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  stepCount: number;
  completedSteps: number;
  failedSteps: number;
  errorDetails?: string;
}

export enum AcknowledgmentState {
  New = 'New',
  Acknowledged = 'Acknowledged',
  Investigating = 'Investigating',
  Identified = 'Identified',
  Resolved = 'Resolved',
}

export interface JobStatusSummary {
  jobId: number;
  status: ExecStatus;
  startedAt?: string;
  finishedAt?: string;
  totalSteps: number;
  pendingSteps: number;
  executingSteps: number;
  completedSteps: number;
  failedSteps: number;
  currentStepIndex?: number;
}

export interface JobAcknowledgment {
  acknowledgmentId: number;
  jobId: number;
  state: AcknowledgmentState;
  userId: number;
  userName: string;
  note?: string;
  updatedAt: string;
}

export interface UpdateAcknowledgmentRequest {
  state: AcknowledgmentState;
  note?: string;
}

export interface StepErrorDetails {
  errorType: string;
  errorMessage: string;
  stderr?: string;
  stdout?: string;
  exitCode?: number;
  stackTrace?: string;
}

export interface JobDetailResponse {
  job: ExecJob;
  routineName: string;
  projectName: string;
  statusSummary: JobStatusSummary;
  steps: ExecStep[];
  stepNames: Record<string, string>;
  acknowledgment?: JobAcknowledgment;
  review?: JobReview;
  /** Routine triggered on approval (workflow chaining) */
  onApproveRoutineId?: number | null;
  /** Name of the routine triggered on approval */
  onApproveRoutineName?: string | null;
  /** Routine triggered on rejection (workflow chaining) */
  onRejectRoutineId?: number | null;
  /** Name of the routine triggered on rejection */
  onRejectRoutineName?: string | null;
}

// ── Review Types (SCRUM-292) ─────────────────────────────────────────────────

export enum ReviewDecision {
  Approved = 'Approved',
  Rejected = 'Rejected',
}

export interface JobReview {
  reviewId: number;
  jobId: number;
  decision: ReviewDecision;
  userId: number;
  note?: string;
  decidedAt: string;
}

export interface ReviewRequest {
  decision: ReviewDecision;
  note?: string;
}

// ── Job Diff Types ────────────────────────────────────────────────────────────

export enum StepMatchType {
  ById = 'byId',
  ByIndex = 'byIndex',
  LeftOnly = 'leftOnly',
  RightOnly = 'rightOnly',
}

export interface DiffStepErrorSummary {
  errorType: string;
  errorMessage: string;
  exitCode?: number;
}

export interface StepSide {
  execStepId: number;
  stepIndex: number;
  status: ExecStatus;
  durationMs?: number;
  errorSummary?: DiffStepErrorSummary;
  hasLogs: boolean;
  hasResults: boolean;
  hasRenders: boolean;
  hasMessages: boolean;
}

export interface StepDiff {
  stepName: string;
  stepId: number;
  matchType: StepMatchType;
  left?: StepSide;
  right?: StepSide;
  statusChanged: boolean;
  durationChangeMs?: number;
  durationChangePct?: number;
  errorChanged: boolean;
}

export interface JobSide {
  jobId: number;
  status: ExecStatus;
  source: string;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  totalSteps: number;
  completedSteps: number;
  failedSteps: number;
  versionId?: number;
  attemptCount: number;
  hasError: boolean;
}

export interface JobDiffResponse {
  routineName: string;
  projectName: string;
  routineId: number;
  left: JobSide;
  right: JobSide;
  versionChanged: boolean;
  steps: StepDiff[];
}
